"use client";

import { useImpersonation } from "~/lib/impersonation-context";
import { api } from "~/trpc/react";
import { useToast } from "~/components/ui/toast";
import { UserIcon, XIcon } from "~/components/icons";
import { getPermissionLabel } from "~/lib/permissions";

export const IMPERSONATION_BANNER_HEIGHT = 40;

export function ImpersonationBanner() {
  const { impersonationData, endImpersonation } = useImpersonation();
  const { showToast } = useToast();

  const endImpersonationMutation = api.auth.endImpersonation.useMutation({
    onSuccess: () => {
      endImpersonation();
      showToast("Impersonation ended. You are now logged in as yourself.", "success");
      window.location.href = "/accounts";
    },
    onError: (error) => {
      showToast(error.message ?? "Failed to end impersonation", "error");
    },
  });

  const handleEndImpersonation = () => {
    if (!impersonationData.impersonatedAccessToken) {
      showToast("Unable to end impersonation: missing token", "error");
      return;
    }

    endImpersonationMutation.mutate({
      accessToken: impersonationData.impersonatedAccessToken,
    });
  };

  if (!impersonationData.isImpersonating || !impersonationData.impersonatedUser) {
    return null;
  }

  const permissionLabel = getPermissionLabel(impersonationData.impersonatedUser.permission);

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[100] bg-[#f5a623] px-4 py-2 shadow-md"
      style={{ height: `${IMPERSONATION_BANNER_HEIGHT}px` }}
    >
      <div className="mx-auto flex h-full items-center justify-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <UserIcon className="h-4 w-4 text-[#1c2024]" />
          <span className="text-sm font-medium text-[#1c2024]">
            Impersonating{" "}
            <strong>{impersonationData.impersonatedUser.username}</strong>
            <span className="text-[#1c2024]/80">
              {" "}({permissionLabel})
            </span>
          </span>
        </div>
        <button
          onClick={handleEndImpersonation}
          disabled={endImpersonationMutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#1c2024] px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-[#2a3038] disabled:opacity-50"
        >
          {endImpersonationMutation.isPending ? (
            "Ending..."
          ) : (
            <>
              <XIcon className="h-3 w-3" />
              End Impersonation
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export function useIsImpersonating(): boolean {
  const { impersonationData } = useImpersonation();
  return impersonationData.isImpersonating;
}

